type FirestoreTimestampLike = {
    toDate?: () => Date;
    toMillis?: () => number;
    seconds?: number;
    nanoseconds?: number;
    _seconds?: number;
    _nanoseconds?: number;
};

// Convert both Firestore SDK timestamps and callable-serialized timestamps into Dates.
export function getFirestoreTimestampDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : new Date(value.getTime());
    }

    const timestamp = value as FirestoreTimestampLike;
    if (typeof timestamp.toDate === 'function') {
        const date = timestamp.toDate();
        return date instanceof Date && !isNaN(date.getTime()) ? date : null;
    }

    if (typeof timestamp.toMillis === 'function') {
        const date = new Date(timestamp.toMillis());
        return isNaN(date.getTime()) ? null : date;
    }

    const seconds = typeof timestamp.seconds === 'number' ? timestamp.seconds : timestamp._seconds;
    const nanoseconds = typeof timestamp.nanoseconds === 'number' ?
        timestamp.nanoseconds : timestamp._nanoseconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const date = new Date((seconds * 1000) + ((nanoseconds || 0) / 1_000_000));
        return isNaN(date.getTime()) ? null : date;
    }

    if (typeof value !== 'string' && typeof value !== 'number') {
        return null;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

// Use the first valid timestamp so malformed legacy primary fields do not hide good fallbacks.
export function getFirstFirestoreTimestampDate(...values: unknown[]): Date | null {
    for (const value of values) {
        const date = getFirestoreTimestampDate(value);
        if (date) {
            return date;
        }
    }

    return null;
}

// Restore timestamps flattened by Firebase callable-result serialization at the data boundary.
export function reviveCallableFirestoreTimestamps<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((item) => reviveCallableFirestoreTimestamps(item)) as T;
    }

    if (typeof value !== 'object' || value === null || value instanceof Date) {
        return value;
    }

    const timestamp = value as FirestoreTimestampLike;
    const keys = Object.keys(value as Record<string, unknown>);
    const hasPrivateTimestampFields =
        typeof timestamp._seconds === 'number' &&
        typeof timestamp._nanoseconds === 'number' &&
        keys.every((key) => key === '_seconds' || key === '_nanoseconds');
    const hasPublicTimestampFields =
        typeof timestamp.seconds === 'number' &&
        typeof timestamp.nanoseconds === 'number' &&
        keys.every((key) => key === 'seconds' || key === 'nanoseconds');
    if (hasPrivateTimestampFields || hasPublicTimestampFields) {
        return (getFirestoreTimestampDate(timestamp) || value) as T;
    }

    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .map(([key, item]) => [key, reviveCallableFirestoreTimestamps(item)])
    ) as T;
}

// Format a local wall-clock value for a native datetime-local editor.
export function formatDateTimeLocalValue(date: Date | null): string {
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Parse a native datetime-local value in the browser's local timezone.
export function parseDateTimeLocalValue(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
    if (!match) {
        return null;
    }

    const [, yearValue, monthValue, dayValue, hourValue, minuteValue] = match;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    const hour = Number(hourValue);
    const minute = Number(minuteValue);
    const date = new Date(0);
    date.setFullYear(year, month - 1, day);
    date.setHours(hour, minute, 0, 0);

    // Reject normalized impossible dates and local times skipped by daylight-saving changes.
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        date.getHours() !== hour ||
        date.getMinutes() !== minute
    ) {
        return null;
    }

    return date;
}
